import { io } from 'socket.io-client';
import { API_BASE_URL } from '../config';

// Create socket instance (not connected initially)
let socket = null;

// Queue of listeners registered before socket is initialized
const pendingListeners = [];

/**
 * Initialize WebSocket connection
 * @param {string} token - Authentication token (omit for public/guest connection)
 */
export const initSocket = (token) => {
  if (socket) {
    // If already connected, disconnect first
    socket.disconnect();
  }

  // Create new socket connection - authenticated or public
  const authOptions = token
    ? { token }
    : { public: true };

  socket = io(API_BASE_URL, {
    auth: authOptions,
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 10000,
  });

  // Connection event handlers
  socket.on('connect', () => {
    console.log('Socket connected');
    // Re-request data on every connect (including reconnects)
    socket.emit('get-market-data');
    socket.emit('get-user-data');
  });

  socket.on('disconnect', () => {
    console.log('Socket disconnected');
  });

  socket.on('connect_error', (error) => {
    console.error('Socket connection error:', error);
  });

  socket.on('error', (error) => {
    console.error('Socket error:', error);
  });

  // Replay any listeners that were registered before the socket was ready
  if (pendingListeners.length > 0) {
    console.log(`[Socket] Replaying ${pendingListeners.length} pending listeners:`, pendingListeners.map(p => p.event));
    pendingListeners.forEach(({ event, callback }) => {
      socket.on(event, callback);
    });
    pendingListeners.length = 0;
  }

  // Debug: log all incoming market-event messages
  socket.onAny((eventName, ...args) => {
    if (eventName === 'market-event') {
      console.log('[Socket RAW] market-event received from server:', args[0]?.type, args[0]?.description);
    }
  });

  // Make socket available globally for components
  window.socket = socket;

  return socket;
};

/**
 * Get the current socket instance
 * @returns {Object|null} - Socket.io instance or null if not connected
 */
export const getSocket = () => {
  return socket;
};

export default {
  get: getSocket,
  init: initSocket,
  connected: () => socket?.connected || false,
  on: (event, callback) => {
    if (socket) {
      console.log(`[Socket] .on('${event}') → attached directly to socket`);
      socket.on(event, callback);
    } else {
      // Queue the listener — it will be replayed when initSocket() is called
      console.log(`[Socket] .on('${event}') → queued (socket not ready)`);
      pendingListeners.push({ event, callback });
    }
  },
  off: (event, callback) => {
    if (socket) {
      socket.off(event, callback);
    }
    // Also remove from pending queue if socket isn't ready yet
    const idx = pendingListeners.findIndex(p => p.event === event && p.callback === callback);
    if (idx !== -1) {
      pendingListeners.splice(idx, 1);
    }
  },
  emit: (event, data) => {
    if (socket) {
      socket.emit(event, data);
    } else {
      console.warn('Socket not initialized. Call initSocket first.');
    }
  },
  disconnect: () => {
    if (socket) {
      socket.disconnect();
      socket = null;
    }
  }
};
